"use client";

import { useState, useEffect, useCallback, memo, useMemo } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReceiptCard } from "./receipt-card";
import {
  getSavedReceipts,
  deleteReceipt,
  areReceiptsEncrypted,
  type SavedReceiptsCollection,
  type SavedReceipt,
} from "@/app/(app)/utils/saved-receipts";
import { useEncryptionPassword } from "@/contexts/encryption-password-context";
import { EncryptionPasswordDialog } from "@/components/password/encryption-password-dialog";
import Link from "next/link";
import { toast } from "sonner";

// Simple Line Separator - Just a horizontal line
const LineSeparator = memo(function LineSeparator() {
  return (
    <div className="w-full px-4">
      <div className="h-px w-full bg-neutral-200" />
    </div>
  );
});

// Memoized EmptyState component to prevent re-creation
const EmptyState = memo(function EmptyState() {
  return (
    <div className="flex flex-col items-center bg-white px-6 pb-24 text-center sm:pb-28">
      {/* Top spacing */}
      <div className="pt-32 sm:pt-48" />

      {/* Prominent Button - Wide */}
      <Link
        href="/create-receipt"
        prefetch={true}
        className="mb-6 w-full max-w-sm"
      >
        <Button
          size="xl"
          className="h-14 w-full rounded-none border border-neutral-900 bg-neutral-900 px-10 text-base font-semibold text-white hover:bg-neutral-800"
        >
          <Plus className="ml-2 h-6 w-6" strokeWidth={2} />
          צור קבלה חדשה
        </Button>
      </Link>

      {/* Subtitle */}
      <p className="max-w-[280px] text-sm leading-relaxed text-neutral-600">
        אין צורך בהרשמה. הוסף את הקבלות שלך כאן, ושלח אותם ללקוחות שלך
      </p>
    </div>
  );
});

// Memoized ReceiptCard wrapper to ensure stable props
const MemoizedReceiptCard = memo(function MemoizedReceiptCard({
  receipt,
  onDelete,
}: {
  receipt: SavedReceipt;
  onDelete: (id: string) => void;
}) {
  return <ReceiptCard receipt={receipt} onDelete={onDelete} />;
});

export function ReceiptsGrid() {
  const [receipts, setReceipts] = useState<SavedReceiptsCollection>({
    receipts: [],
    version: "1.0",
  });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordDialogMode, setPasswordDialogMode] = useState<
    "create" | "enter"
  >("enter");

  const { password, hasPassword, hasBeenPrompted, markPrompted, setPassword } =
    useEncryptionPassword();

  // Load receipts on mount
  useEffect(() => {
    const loadReceipts = async () => {
      // Check if there are encrypted receipts but no password
      const hasEncryptedReceipts = areReceiptsEncrypted();

      if (hasEncryptedReceipts && !hasPassword && !hasBeenPrompted) {
        // Show password dialog to enter existing password
        setPasswordDialogMode("enter");
        setShowPasswordDialog(true);
        markPrompted();
        return;
      }

      const loadedReceipts = await getSavedReceipts(password || undefined);
      setReceipts(loadedReceipts);
    };

    loadReceipts();
  }, [password, hasPassword, hasBeenPrompted, markPrompted]);

  // Handle password submission
  const handlePasswordSubmit = useCallback(
    async (submittedPassword: string) => {
      setPassword(submittedPassword);
      setShowPasswordDialog(false);

      // Reload receipts with the new password
      try {
        const loadedReceipts = await getSavedReceipts(submittedPassword);
        setReceipts(loadedReceipts);

        if (loadedReceipts.receipts.length === 0 && areReceiptsEncrypted()) {
          // Password might be wrong - show error
          toast.error("לא ניתן לפתוח את הקבלות", {
            description: "ייתכן שהסיסמה שגויה. אנא נסה שוב.",
          });
        }
      } catch (error) {
        toast.error("שגיאה בטעינת הקבלות");
        console.error("Error loading receipts with password:", error);
      }
    },
    [setPassword],
  );

  // Handle delete - stable callback using functional setState
  const handleDelete = useCallback(
    async (id: string) => {
      if (!password) {
        // Need password to delete (for re-encryption)
        setPasswordDialogMode(hasBeenPrompted ? "enter" : "create");
        setShowPasswordDialog(true);
        toast.error("נדרשת סיסמה למחיקת הקבלה");
        return;
      }

      try {
        await deleteReceipt(id, password);
        const updatedReceipts = await getSavedReceipts(password);
        setReceipts(updatedReceipts);
        toast.success("הקבלה נמחקה בהצלחה");
      } catch (error) {
        toast.error("שגיאה במחיקת הקבלה");
        console.error("Error deleting receipt:", error);
      }
    },
    [password, hasBeenPrompted],
  );

  // Memoize the receipts list to prevent unnecessary re-renders
  const receiptCards = useMemo(
    () =>
      receipts.receipts.map((receipt: SavedReceipt) => (
        <MemoizedReceiptCard
          key={receipt.id}
          receipt={receipt}
          onDelete={handleDelete}
        />
      )),
    [receipts.receipts, handleDelete],
  );

  // Empty state - uses memoized component with new layout
  if (receipts.receipts.length === 0) {
    return (
      <>
        <EmptyState />
        <EncryptionPasswordDialog
          isOpen={showPasswordDialog}
          onClose={() => setShowPasswordDialog(false)}
          onSubmit={handlePasswordSubmit}
          mode={passwordDialogMode}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col bg-white pb-24 sm:pb-28 lg:pr-16">
        {/* Top Section: Just Button - All White Background */}
        <div className="flex flex-col items-center px-4 pt-32 pb-6 sm:pt-48">
          {/* Prominent Button - Wide */}
          <Link
            href="/create-receipt"
            prefetch={true}
            className="mb-2 w-full max-w-sm"
          >
            <Button
              size="lg"
              className="h-14 w-full rounded-none border border-neutral-900 bg-neutral-900 px-10 text-base font-semibold text-white hover:bg-neutral-800"
            >
              <Plus className="ml-2 h-5 w-5" strokeWidth={2} />
              קבלה חדשה
            </Button>
          </Link>
        </div>

        {/* Simple Line Separator */}
        <div className="py-6">
          <LineSeparator />
        </div>

        {/* Bottom Section: Receipts Grid - All White */}
        <div className="flex-1 bg-white px-4 pb-4">
          <div className="content-visibility-auto grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {receiptCards}

            {/* Bottom padding spacer for mobile navigation */}
            <div className="col-span-full h-16 sm:h-20" />
          </div>
        </div>
      </div>

      <EncryptionPasswordDialog
        isOpen={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        onSubmit={handlePasswordSubmit}
        mode={passwordDialogMode}
      />
    </>
  );
}
